import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const FeatureRichTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-amber-50/40 text-slate-800"
      style={{
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl border border-amber-200/80 overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Comprehensive Matrix */}
        <div className="md:col-span-6 p-6 md:p-8 bg-gradient-to-b from-amber-50/80 to-orange-50/60 border-b md:border-b-0 md:border-r border-amber-200/60 space-y-6">
          <div className="space-y-3">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />}
            <span className="px-2.5 py-0.5 bg-amber-200 text-amber-900 text-[10px] font-black rounded-full uppercase">
              Full Feature Matrix
            </span>
            <h2 className="text-2xl font-black text-slate-900">{plan.tier} Value Package</h2>
            {plan.description && <p className="text-xs text-slate-600 leading-relaxed">{plan.description}</p>}
          </div>

          <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />

          <div className="space-y-2">
            <p className="text-[11px] font-black uppercase text-amber-900 tracking-wider">Everything included in this tier:</p>
            <PlanFeatureList features={plan.features} checkColorClass="text-amber-600" />
          </div>

          {config.showTestimonial && <TestimonialCard testimonial={config.testimonial} />}

          <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
        </div>

        {/* Right Checkout Form */}
        <div className="md:col-span-6 p-6 md:p-8 space-y-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{config.headerMain || 'Billing Information'}</h1>
            <p className="text-xs text-slate-500">{config.headerSub || 'Quick setup with instant provisioning.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields config={config} formData={formData} onInputChange={onInputChange} />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 text-xs font-black uppercase tracking-wider text-white shadow-lg transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-50"
              style={{
                backgroundColor: config.primaryColor || '#d97706',
                borderRadius: config.buttonRadius || '8px',
              }}
            >
              {isCapturing ? 'Activating Matrix...' : config.buttonText || `Subscribe for ${currencySymbol}${finalTotal.toFixed(2)}`}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
